// Tiny, dependency-free IP comparison helpers — split out of webrtcLeak.ts (which re-exports them for
// its existing callers) specifically so callers that only need "are these the same address" don't
// have to pull in the rest of that module. webrtcLeak.ts is deliberately kept out of eager bundles
// (dynamically imported by homeQuickChecks.ts, environmentCheck.ts, etc. — see those modules'
// comments) precisely because it's larger than the pages that use it; importing it statically just
// for address comparison would defeat that.
export function addressesEqual(left: string, right: string): boolean {
  return normalizeAddress(left) === normalizeAddress(right);
}

export function normalizeAddress(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!normalized.includes(":")) return normalized;
  try {
    // URL parsing canonicalizes equivalent compressed and expanded IPv6 spellings.
    return new URL(`http://[${normalized}]/`).hostname.replace(/^\[|\]$/g, "");
  } catch {
    return normalized;
  }
}

// spec §二十一: whether a single URL path segment is a literal IPv4 address, so `/1.1.1.1` can route
// straight into a query while `/about.html`, `/zh-Hans`, `/blog` and every other one-segment route keep
// working. Deliberately strict — four dot-separated decimal octets, no leading zeros (which some
// resolvers read as octal), nothing else — because this predicate is what decides whether a segment is
// handed to the query API at all: `/999.1.1.1` and `/1.1.1.1.html` must both fall through to the normal
// router rather than reaching a provider (spec §二十一: "非法地址不能执行 Provider 查询").
//
// IPv6 is intentionally not accepted here. A bare `:`-bearing segment is awkward in a URL and ambiguous
// against a port, so IPv6 keeps its existing query path (spec §二十一 says so explicitly).
export function isIpv4Address(value: string): boolean {
  const octets = value.split(".");
  if (octets.length !== 4) return false;

  return octets.every((octet) => {
    if (!/^\d{1,3}$/.test(octet)) return false;
    if (octet.length > 1 && octet.startsWith("0")) return false;
    return Number(octet) <= 255;
  });
}

// Whether `value` is a single, literal IPv6 address — no CIDR, no zone id, no surrounding text. Reuses
// the same bracketed-URL parse normalizeAddress above already relies on: the WHATWG URL parser only
// accepts a bracketed host when it is a syntactically valid IPv6 literal (RFC 3986 IP-literal), so it
// rejects CIDR suffixes, zone ids (`%eth0`), embedded delimiters, and non-address text as a side effect
// of being spec-compliant — no separate regex to keep in sync with IPv6's grammar. Requires a literal
// `:` up front purely so a plain hostname or IPv4 address (neither of which contains one) fails fast
// without allocating a URL.
export function isIpv6Address(value: string): boolean {
  if (!value.includes(":")) return false;
  try {
    new URL(`http://[${value}]/`);
    return true;
  } catch {
    return false;
  }
}
